import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Row,
  Col,
  Card,
  Statistic,
  Progress,
  Tag,
  Space,
  DatePicker,
  Button,
  Typography,
  Divider,
  Tooltip,
  Spin,
  Alert,
} from 'antd';
import {
  TrophyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileDoneOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchVendorPerformanceRequest,
} from '../../redux/actions/vendorPerformanceActions';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// ── Grade helpers ─────────────────────────────────────────────────────────────

const GRADE_CONFIG = {
  A: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Excellent' },
  B: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Good'      },
  C: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', label: 'Average'   },
  D: { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Below Avg' },
  F: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffadd2', label: 'Poor'      },
};

export const getGradeTag = (grade, size = 'default') => {
  const cfg = GRADE_CONFIG[grade] || { color: '#8c8c8c', bg: '#f5f5f5', border: '#d9d9d9', label: '—' };
  return (
    <Tag
      style={{
        background: cfg.bg,
        borderColor: cfg.border,
        color: cfg.color,
        fontWeight: 700,
        fontSize: size === 'large' ? 18 : 13,
        padding: size === 'large' ? '4px 16px' : undefined,
      }}
    >
      {grade || '—'}
    </Tag>
  );
};

const getScoreColor = (score) => {
  if (score >= 90) return '#52c41a';
  if (score >= 75) return '#1890ff';
  if (score >= 60) return '#faad14';
  if (score >= 45) return '#fa8c16';
  return '#ff4d4f';
};

// ── MetricCard ────────────────────────────────────────────────────────────────

const MetricCard = ({ title, value, suffix = '%', color, icon, description, tooltip }) => (
  <Card size="small" style={{ height: '100%' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ color, fontSize: 16 }}>{icon}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {title}
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 11, color: '#8c8c8c' }} />
              </Tooltip>
            )}
          </Text>
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>
          {value != null ? `${Number(value).toFixed(1)}${suffix}` : '—'}
        </div>
        {description && (
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            {description}
          </Text>
        )}
      </div>
    </div>
    <Progress
      percent={Math.min(100, Number(value) || 0)}
      showInfo={false}
      size="small"
      strokeColor={color}
      trailColor="#f0f0f0"
      style={{ marginTop: 12, marginBottom: 0 }}
    />
  </Card>
);

// ── Main Component ────────────────────────────────────────────────────────────

/**
 * VendorPerformanceDetail
 *
 * Can be used as a standalone section OR inside a Modal.
 * Props:
 *  vendorId    — uint
 *  vendorName  — string (for title)
 *  open        — boolean (modal mode)
 *  onClose     — callback (modal mode)
 *  inlineMode  — boolean (render without modal wrapper)
 */
const VendorPerformanceDetail = ({ vendorId, vendorName, open, onClose, inlineMode = false }) => {
  const dispatch = useDispatch();
  const { vendorScores, loading } = useSelector((s) => s.vendorPerformance);

  const [dateRange, setDateRange] = useState(null);

  const score = vendorScores[vendorId];

  const fetchScore = () => {
    if (!vendorId) return;
    const params = {};
    if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.date_to   = dateRange[1].format('YYYY-MM-DD');
    dispatch(fetchVendorPerformanceRequest(vendorId, params));
  };

  useEffect(() => {
    if (vendorId && (open || inlineMode)) {
      fetchScore();
    }
  }, [vendorId, open, inlineMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const content = (
    <div>
      {/* ── Date filter ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <RangePicker
          format="DD MMM YYYY"
          value={dateRange}
          onChange={setDateRange}
          allowClear
          placeholder={['From Date', 'To Date']}
          style={{ flex: 1, minWidth: 240 }}
        />
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={fetchScore}
          loading={loading}
        >
          Apply
        </Button>
        {dateRange && (
          <Button onClick={() => { setDateRange(null); }}>
            All Time
          </Button>
        )}
      </div>

      {loading && !score ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : !score ? null : score.total_orders === 0 ? (
        <Alert
          type="info"
          showIcon
          message="No data available for this period"
          description="This vendor has no purchase orders in the selected date range."
        />
      ) : (
        <>
          {/* ── Overall Score Hero ────────────────────────────────────── */}
          <div
            style={{
              textAlign: 'center',
              padding: '20px 0 16px',
              background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)',
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #f0f0f0',
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Overall Performance Score</Text>
            </div>
            <div
              style={{
                fontSize: 52,
                fontWeight: 800,
                color: getScoreColor(score.overall_score),
                lineHeight: 1,
              }}
            >
              {Number(score.overall_score).toFixed(1)}
            </div>
            <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 12 }}>/ 100</div>
            <Space size={8}>
              {getGradeTag(score.score_grade, 'large')}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {GRADE_CONFIG[score.score_grade]?.label || ''}
              </Text>
            </Space>
            <div style={{ marginTop: 16, padding: '0 40px' }}>
              <Progress
                percent={score.overall_score}
                strokeColor={getScoreColor(score.overall_score)}
                showInfo={false}
                strokeWidth={8}
              />
            </div>
          </div>

          {/* ── Order counts ────────────────────────────────────────────── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {[
              { label: 'Total Orders',  value: score.total_orders,   color: '#1890ff' },
              { label: 'Total Challans', value: score.total_challans, color: '#13c2c2' },
              { label: 'Total Bills',   value: score.total_bills,    color: '#722ed1' },
            ].map((s) => (
              <Col xs={8} key={s.label}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>{s.label}</Text>
                </Card>
              </Col>
            ))}
          </Row>

          <Divider style={{ margin: '8px 0 16px' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Performance Metrics</Text>
          </Divider>

          {/* ── 4 Metric Cards ──────────────────────────────────────────── */}
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12}>
              <MetricCard
                title="On-Time Delivery Rate"
                value={score.on_time_delivery_rate}
                color={getScoreColor(score.on_time_delivery_rate)}
                icon={<ClockCircleOutlined />}
                description="Challans delivered by PO deadline"
                tooltip="Excludes challans whose PO had no delivery_date set — those are not penalised."
              />
            </Col>
            <Col xs={24} sm={12}>
              <MetricCard
                title="Quantity Accuracy Rate"
                value={score.quantity_accuracy_rate}
                color={getScoreColor(score.quantity_accuracy_rate)}
                icon={<CheckCircleOutlined />}
                description="Accepted qty ÷ ordered qty"
              />
            </Col>
            <Col xs={24} sm={12}>
              <MetricCard
                title="Quality Rejection Rate"
                value={score.quality_rejection_rate}
                color={score.quality_rejection_rate <= 5 ? '#52c41a' : score.quality_rejection_rate <= 15 ? '#faad14' : '#ff4d4f'}
                icon={<WarningOutlined />}
                description="Rejected qty ÷ delivered qty (lower is better)"
                tooltip="A lower rejection rate means better quality goods from this vendor."
              />
            </Col>
            <Col xs={24} sm={12}>
              <MetricCard
                title="Bill Accuracy Rate"
                value={score.bill_accuracy_rate}
                color={getScoreColor(score.bill_accuracy_rate)}
                icon={<FileDoneOutlined />}
                description="Bills fully matched to PO"
              />
            </Col>
          </Row>

          {/* ── Weight breakdown note ────────────────────────────────────── */}
          <div
            style={{
              marginTop: 16,
              padding: '10px 14px',
              background: '#fafafa',
              borderRadius: 6,
              border: '1px solid #f0f0f0',
            }}
          >
            <Text type="secondary" style={{ fontSize: 11 }}>
              <strong>Score formula:</strong> On-Time (35%) + Qty Accuracy (25%) + Quality (20%) + Bill Accuracy (20%)
            </Text>
          </div>

          {/* ── Date range note ──────────────────────────────────────────── */}
          {(score.date_from || score.date_to) && (
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Period: {score.date_from || '—'} → {score.date_to || '—'}
              </Text>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (inlineMode) return content;

  return (
    <Modal
      title={
        <Space>
          <TrophyOutlined style={{ color: '#faad14' }} />
          Performance — {vendorName || `Vendor #${vendorId}`}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={680}
      destroyOnClose
    >
      {content}
    </Modal>
  );
};

export default VendorPerformanceDetail;
